import BigNumber from "bignumber.js";
import type { CampaignsType } from "@/hooks/firestore/models/bettingEvent";
import { identity } from "lodash";
import { isFuture } from "date-fns";
import { OUTCOME_NAMES } from "@/features/betting/components/outcomes/Outcomes";
import type { FranchiseType, OutcomeType } from "types/BetTypes";
import type { GroupedTokens } from "@/hooks/firestore/user/useTokens";
import type { BettingType, EventStatus } from "@/hooks/firestore/betting/types";
import type { Permissions } from "@/features/auth/models/permissions";
import type {
  ProcessedBettingCompetitor,
  ProcessedMarket,
  ProcessedOutcome,
  ProcessedOutcomes,
} from "hooks/firestore/betting/useEvent";

const BASE_ICON_URL = "https://res.cloudinary.com/skrilla/image/upload";

// Accepts any event-type string: sports events, selections and betslip
// entries each carry their own (partly overlapping) eventType unions, and this
// is only a membership check against the outright set.
export const isOutright = (eventType: string): boolean => {
  return ["TOURNAMENT", "SEASON", "OUTRIGHT"].includes(eventType);
};

// Combo multi label by legs required to win: 2 = Double, 3 = Treble, 4+ = N-Fold
const COMBO_LABELS: Record<number, string> = {
  2: "Doubles",
  3: "Trebles",
};

export const getComboLabel = (legsToWin: number) =>
  COMBO_LABELS[legsToWin] ?? `${legsToWin}-Folds`;

/** Takes in a betting event and returns the home, away & draw outcomes which
 * are extracted from the main market object
 */
export const getHeadToHeadOutcomes = (
  // Only reads `mainMarket`, so accept any processed event (feed or detail).
  event: { mainMarket?: ProcessedMarket },
  mainMarketOverride?: ProcessedMarket,
): {
  homeOutcome?: ProcessedOutcome;
  awayOutcome?: ProcessedOutcome;
  drawOutcome?: ProcessedOutcome;
} => {
  const mainMarket = mainMarketOverride || event.mainMarket;
  if (!mainMarket) return {};

  const outcomeEntries = Object.entries(mainMarket.outcomes || {});

  const [, homeOutcome] =
    outcomeEntries.find(
      ([_, outcome]) =>
        typeof outcome.type === "string" && outcome.type.endsWith("HOME"),
    ) ?? [];

  const [, awayOutcome] =
    outcomeEntries.find(
      ([_, outcome]) =>
        typeof outcome.type === "string" && outcome.type.endsWith("AWAY"),
    ) ?? [];

  const [, drawOutcome] =
    outcomeEntries.find(
      ([_, outcome]) =>
        typeof outcome.type === "string" && outcome.type === "DRAW",
    ) ?? [];

  return {
    homeOutcome: homeOutcome && { ...homeOutcome },
    awayOutcome: awayOutcome && { ...awayOutcome },
    drawOutcome: drawOutcome && { ...drawOutcome },
  };
};

export const getDefaultBettingPath = (defaultHub: string) =>
  defaultHub === "racing"
    ? "/racing/betting/"
    : defaultHub === "sports"
      ? "/sports/betting/"
      : "/betting/";

export const mapCampaigns = (
  campaigns: any,
  userCampaigns: string[],
  permissions: Record<string, string>,
) =>
  permissions["viewPromotion"] === "GRANTED" &&
  (campaigns as CampaignsType[])?.reduce((acc, value) => {
    if (value?.openToAllEligibleUsers || userCampaigns.includes(value.id)) {
      acc.push(value);
    }

    return acc;
  }, [] as CampaignsType[]);

export const getIsSGMReady = (sgmAvailableAtDate: Date | undefined) => {
  if (!sgmAvailableAtDate) return;

  return new Date() > sgmAvailableAtDate;
};

/** Live markets that opened as prematch stay available to users without live
 * access - the prematchMarket flag marks them. */
export const isLiveBlocked = (
  market:
    | { bettingType?: BettingType; prematchMarket?: boolean }
    | null
    | undefined,
  canViewLiveMarkets: boolean,
) =>
  market?.bettingType === "Live" &&
  !market?.prematchMarket &&
  !canViewLiveMarkets;

/**
 * Live without live permission: visible, locked, closing price only.
 * `isLiveBlocked` decides if a market shows at all, this decides if it's
 * bettable. Ends with the match, and the market's own type catches one that
 * goes live first.
 */
export const isLiveViewOnly = (
  canViewLiveMarkets: boolean,
  eventStatus?: EventStatus,
  bettingType?: BettingType,
) =>
  !canViewLiveMarkets &&
  eventStatus !== "SETTLED" &&
  eventStatus !== "CANCELLED" &&
  (bettingType === "Live" || eventStatus === "LIVE");

/** The one list of statuses that take bets - `isMarketInactive` is its
 * inverse. LIVE is one: `processMarket` folds a trading live market to it. */
export const isMarketTrading = (status?: string) =>
  status === "ACTIVE" || status === "LIVE";

/** The price to show on the page, or sync into the betslip. `undefined` means
 * it can't be trusted right now (suspended mid-trade) - the page dashes it,
 * the betslip just skips the write and keeps the last price it had. */
export const getEffectiveOdds = ({
  odds,
  closingOdds,
  active,
  marketStatus,
  isLiveViewOnly,
}: {
  odds?: number;
  closingOdds?: number;
  active?: boolean;
  marketStatus?: string;
  isLiveViewOnly?: boolean;
}): number | undefined => {
  // View-only gets the closing price or a dash, never live. First, or the
  // closed branch below falls back to live.
  if (isLiveViewOnly) {
    return closingOdds || undefined;
  }

  // Genuinely closed - show the price it closed at.
  if (marketStatus === "SETTLED" || marketStatus === "DEACTIVATED") {
    return closingOdds ?? odds;
  }

  // Suspended mid-trade - hide the price rather than show a stale one.
  if (
    !active ||
    marketStatus === "UNAVAILABLE" ||
    marketStatus === "SUSPENDED"
  ) {
    return undefined;
  }

  return odds;
};

/** Odds for the match list and match page. 0 means dash. `Odds` already
 *  dashes anything <= 1. */
export const getDisplayOdds = (
  params: Parameters<typeof getEffectiveOdds>[0],
): number => getEffectiveOdds(params) ?? 0;

/** Live statuses on a SUBMITTED selection - the bet response vocabulary, which
 * has WAITING. Feed statuses never do, so check those against "LIVE". */
export const isLiveStatus = (status?: string) =>
  status === "LIVE" || status === "WAITING";

/**
 * Whether a status can carry a bet: live ones need the permission, the rest are
 * open to everyone.
 */
export const isBettableStatus = (
  status: string | undefined,
  canViewLiveMarkets: boolean,
) =>
  isLiveStatus(status)
    ? canViewLiveMarkets
    : status === "ACTIVE" || status === "OPEN";

const isUserExcludedFromCampaign = (
  userCampaigns: string[],
  excludedCampaigns: string[] = [],
) => {
  return (userCampaigns ?? []).some((id) => excludedCampaigns.includes(id));
};

const hasValidTokenAllocation = (
  userTokens: GroupedTokens,
  automationId?: string,
) => {
  if (!automationId) return false;

  return Object.values(userTokens)?.some(({ tokens }) =>
    tokens.some(({ automationId: id, allocations }) => {
      if (!allocations) return false;

      const expiresAt = allocations.find(
        ({ automationId: id }) => automationId === id,
      )?.expiresAt!;

      return id === automationId && isFuture(expiresAt);
    }),
  );
};

export const shouldShowPromoBanner = (
  campaign?: CampaignsType,
  userCampaigns: string[] = [],
  userTokens: GroupedTokens = {},
  permissions: Permissions = {},
): boolean => {
  if (!campaign || permissions["viewPromotion"] !== "GRANTED") return false;

  if (
    isUserExcludedFromCampaign(
      userCampaigns,
      campaign?.automation?.excludedCampaignIds,
    )
  ) {
    return false;
  }

  if (campaign.automation?.type === "TOKEN") {
    return hasValidTokenAllocation(userTokens, campaign.automation?.id);
  }

  if (campaign.openToAllEligibleUsers) return true;

  return userCampaigns.includes(campaign.id);
};

export const marketSearch = ({
  markets,
  searchFilter,
  mainMarketStatus,
  isLiveViewOnly = false,
}: {
  markets: ProcessedMarket[];
  searchFilter: string;
  mainMarketStatus?: string;
  isLiveViewOnly?: boolean;
}): ProcessedMarket[] => {
  if (!markets) {
    return [];
  }

  const validMarkets = markets.flatMap((market) => {
    //handicap needs left/right to stay aligned, so keep as-is
    if (market?.marketType?.includes("HANDICAP")) {
      return [market];
    }

    // Strip inactive outcomes while the main market trades, and drop the
    // market if none are left. View-only keeps them all, and so does a
    // suspended market with nothing active - any more and suspending would
    // reveal lines the open market hides.
    const outcomes = Object.entries(market.outcomes || {});
    const isFullySuspended =
      market.status === "SUSPENDED" &&
      outcomes.every(([, outcome]) => !outcome.active);
    const validOutcomes = Object.fromEntries(
      outcomes.filter(
        ([, outcome]) =>
          isLiveViewOnly ||
          isFullySuspended ||
          !isMarketTrading(mainMarketStatus) ||
          outcome.active,
      ),
    );

    return Object.keys(validOutcomes).length > 0
      ? [{ ...market, outcomes: validOutcomes }]
      : [];
  });

  if (searchFilter === "") {
    return validMarkets;
  }

  const searchFilterLower = searchFilter.toLowerCase();

  return validMarkets.reduce((_markets, _market) => {
    const filteredOutcomes = Object.entries(_market.outcomes || {}).reduce(
      (acc, [outcomeId, outcome]) => {
        if (outcome?.name?.toLowerCase().includes(searchFilterLower)) {
          acc[outcomeId] = outcome;
        }
        return acc;
      },
      {} as Record<string, ProcessedOutcome>,
    );

    if (
      Object.keys(_market.outcomes || {}).length > 0 &&
      _market.name.toLowerCase().includes(searchFilterLower)
    ) {
      _markets.push(_market);
    } else if (
      Object.keys(filteredOutcomes || {}).length > 0 &&
      _market?.marketType?.includes("HANDICAP")
    ) {
      _markets.push(_market);
    } else if (Object.keys(filteredOutcomes || {}).length > 0) {
      _markets.push({ ..._market, outcomes: filteredOutcomes });
    }

    return _markets;
  }, [] as ProcessedMarket[]);
};

// keyed by side, since the team filter needs to tell them apart
export type MatchTeams = {
  home?: ProcessedBettingCompetitor;
  away?: ProcessedBettingCompetitor;
};

// home first, dropping whichever side the event doesn't have
export const getTeamList = (teams?: MatchTeams): ProcessedBettingCompetitor[] =>
  [teams?.home, teams?.away].filter(
    (team): team is ProcessedBettingCompetitor => !!team,
  );

export type MainMarket = { outcomes: ProcessedOutcomes };

const SUSPENDED_MARKET_STATUSES = ["SUSPENDED", "DEACTIVATED", "UNAVAILABLE"];

export const isSuspendedMarketStatus = (status?: string | null): boolean =>
  SUSPENDED_MARKET_STATUSES.includes(status ?? "");

// resolve the team/franchise competitor id for an outcome on demand, without
// mutating what firestore gives us. player home/away outcomes borrow the
// franchise from the main market; everything else uses its own competitorId.
export const getOutcomeTeamCompetitorId = (
  outcome?: Pick<ProcessedOutcome, "type" | "competitorId">,
  mainMarket?: MainMarket | null,
): string | undefined => {
  if (!outcome) return undefined;

  const franchiseType = outcome.type?.includes("PLAYER_AWAY")
    ? "FRANCHISE_AWAY"
    : outcome.type?.includes("PLAYER_HOME")
      ? "FRANCHISE_HOME"
      : null;

  if (franchiseType && mainMarket) {
    const franchiseId = Object.values(mainMarket.outcomes).find(
      (o) => o.type === franchiseType,
    )?.competitorId;
    if (franchiseId) return franchiseId;
  }

  return outcome.competitorId;
};

// which team an outcome belongs to, or null if it's match-level (draw, totals).
// standard markets carry the team via competitorId; some markets (player props,
// winning margin) only signal the side via the outcome type suffix (_HOME/_AWAY).
const resolveOutcomeTeam = (
  outcome: ProcessedOutcome,
  teams: MatchTeams,
): string | null => {
  const homeTeamId = teams.home?.id;
  const awayTeamId = teams.away?.id;

  if (
    outcome.competitorId &&
    (outcome.competitorId === homeTeamId || outcome.competitorId === awayTeamId)
  ) {
    return outcome.competitorId;
  }
  // some markets (player props, winning margin) only carry the side in the
  // type, as a suffix - the same read the outcome components do. anchored, so
  // a type that merely contains the word (BATTER_HOME_RUNS) isn't a side
  if (outcome.type?.endsWith("_HOME")) return homeTeamId ?? null;
  if (outcome.type?.endsWith("_AWAY")) return awayTeamId ?? null;
  return null;
};

const filterMarketsByTeam = (
  markets: ProcessedMarket[],
  selectedTeamId: string | null,
  teams: MatchTeams,
): ProcessedMarket[] => {
  if (!selectedTeamId) return markets;

  return markets.reduce<ProcessedMarket[]>((acc, market) => {
    const kept = Object.entries(market.outcomes || {}).filter(
      ([, outcome]) => resolveOutcomeTeam(outcome, teams) === selectedTeamId,
    );

    // nothing for this team - drop the market (and its group, if empty)
    if (kept.length === 0) return acc;

    acc.push({ ...market, outcomes: Object.fromEntries(kept) });
    return acc;
  }, []);
};

export const filterMarketsByType = (
  markets: ProcessedMarket[],
  marketTypes: string[] | null,
): ProcessedMarket[] => {
  if (!marketTypes?.length) return markets;

  return markets.filter((market) => marketTypes.includes(market.marketType));
};

// labels of filter options with no matching markets on this match
export const getEmptyMarketFilterLabels = (
  markets: ProcessedMarket[],
  options: { label: string; marketTypes: string[] }[],
) => {
  const presentTypes = new Set(markets.map((market) => market.marketType));

  return new Set(
    options.flatMap((option) =>
      option.marketTypes.some((type) => presentTypes.has(type))
        ? []
        : [option.label],
    ),
  );
};

export type MarketFilterCriteria = {
  teamId: string | null;
  marketTypes: string[] | null;
};

// compose the post-search market filters. text search is applied separately
// because the market set it runs over depends on the active tab.
export const applyMarketFilters = (
  markets: ProcessedMarket[],
  { teamId, marketTypes }: MarketFilterCriteria,
  teams: MatchTeams,
): ProcessedMarket[] =>
  filterMarketsByType(filterMarketsByTeam(markets, teamId, teams), marketTypes);

export const buildCloudinaryTransformation = (
  entity: Competitor | FranchiseType | any,
  size: number,
) => {
  const transformation = [];

  if (!entity?.iconUri) return null;

  if (size) {
    const baseSized = parseInt(size as any, 10);
    transformation.push(`w_${baseSized}`);
    transformation.push(`h_${baseSized}`);
  }

  if (entity?.type?.toLowerCase() === "PLAYER") {
    transformation.push("c_thumb");
    transformation.push("g_face");
    transformation.push("e_sharpen");
  } else if (entity?.type?.toLowerCase() === "FRANCHISE") {
    transformation.push("f_auto");
    transformation.push("q_auto");
  } else {
    transformation.push("f_auto");
    transformation.push("q_auto");
  }

  const finalUrl = entity.iconUri.replace(
    BASE_ICON_URL,
    `${BASE_ICON_URL}/${transformation.join(",")}`,
  );

  return finalUrl;
};

const findNum = /[-+]?[0-9]+\.?[0-9]*/;

const createDummyOutcome = (
  outcome: ProcessedOutcome,
  value: string,
  pairs: string[] = ["OVER", "UNDER"],
): ProcessedOutcome => {
  let abbreviation = outcome?.abbreviation;
  // Check if value contains one of the pairs (case-insensitive)
  const matchedPair = pairs.find((pair) =>
    value.toLowerCase().includes(pair.toLowerCase()),
  );

  if (matchedPair) {
    abbreviation = value;
  } else {
    abbreviation = abbreviation?.replace(
      outcome?.abbreviation?.match(findNum)?.[0] ?? "",
      value,
    );
  }

  const name = outcome?.name?.replace(
    outcome.name.match(findNum)?.[0] ?? "",
    value,
  );

  return {
    ...outcome,
    id: `${value}-${name}`,
    name: name,
    abbreviation: abbreviation,
    odds: 1,
    // placeholders have no price, not even a closing one
    closingOdds: undefined,
    active: false,
    result: "UNDECIDED",
    type: matchedPair
      ? (matchedPair.toUpperCase() as OutcomeType)
      : outcome.type,
  };
};

const oppositeValue = (value: string, pairs: string[]) => {
  const [pair1, pair2] = pairs.map((pair) => pair.toLowerCase());

  const matchPair1 = value.match(new RegExp(pair1, "i"));
  const matchPair2 = value.match(new RegExp(pair2, "i"));

  if (matchPair1) {
    const firstCharCase =
      matchPair1[0].charAt(0) === matchPair1[0].charAt(0).toUpperCase()
        ? "uppercase"
        : "lowercase";
    const replacement =
      firstCharCase === "uppercase"
        ? pair2.charAt(0).toUpperCase() + pair2.slice(1).toLowerCase()
        : pair2.toLowerCase();
    return value.replace(new RegExp(pair1, "i"), replacement);
  } else if (matchPair2) {
    const firstCharCase =
      matchPair2[0].charAt(0) === matchPair2[0].charAt(0).toUpperCase()
        ? "uppercase"
        : "lowercase";
    const replacement =
      firstCharCase === "uppercase"
        ? pair1.charAt(0).toUpperCase() + pair1.slice(1).toLowerCase()
        : pair1.toLowerCase();
    return value.replace(new RegExp(pair2, "i"), replacement);
  }

  const numMatch = value.match(/[-+]?\d+(\.\d+)?/);
  if (numMatch) {
    const bNum = new BigNumber(numMatch[0]);
    if (numMatch[0].startsWith("+") || !numMatch[0].startsWith("-")) {
      return value.replace(numMatch[0], "-" + bNum.abs().toString());
    } else {
      return value.replace(numMatch[0], "+" + bNum.abs().toString());
    }
  }

  return value;
};

const generateOppositeOutcomes = (
  firstOutcomeValues: string[],
  secondOutcomeValues: string[],
  pairs: string[],
): { first: string; second: string }[] => {
  const result = [];

  for (const first of firstOutcomeValues) {
    const opposite = secondOutcomeValues.find(
      (value) => value === oppositeValue(first, pairs),
    );

    if (opposite !== undefined) {
      result.push({ first: first, second: opposite });
      secondOutcomeValues = secondOutcomeValues.filter(
        (value) => value !== opposite,
      );
    } else {
      result.push({
        first: first,
        second: oppositeValue(first, pairs),
      });
    }
  }

  for (const second of secondOutcomeValues) {
    result.push({ first: oppositeValue(second, pairs), second: second });
  }

  return result.sort((a, b) => {
    // sort by first number
    const aFirstNumber = parseFloat(a.first.match(findNum)?.[0] ?? "0");
    const bFirstNumber = parseFloat(b.first.match(findNum)?.[0] ?? "0");

    return aFirstNumber - bFirstNumber;
  });
};

const TEAM_NAMES_WITH_NUMS = {
  "49ers": "Forty Niners",
  "76ers": "Seventy Sixers",
  "36ers": "Thirty Sixers",
};
type TeamNamesWithNums = keyof typeof TEAM_NAMES_WITH_NUMS;

export const sortPairedOutcomes = (
  outcomes: ProcessedOutcomes,
  pairs: string[] = ["UNDER", "OVER"],
  stringOpposites = false,
  showInactive = false,
): ProcessedOutcome[] => {
  if (pairs.length !== 2) {
    console.error("The pairs array must have exactly two elements.");
    return [];
  }

  const makeOutcomesNamesNumberSafe = (
    outcomes: ProcessedOutcomes,
  ): ProcessedOutcome[] => {
    return Object.entries(outcomes).map(([id, outcome]) => {
      return {
        ...outcome,
        id,
        name: Object.keys(TEAM_NAMES_WITH_NUMS).reduce(
          (acc, teamName) =>
            acc.replace(
              teamName,
              TEAM_NAMES_WITH_NUMS[teamName as TeamNamesWithNums],
            ),
          outcome.name,
        ),
        abbreviation: outcome.abbreviation
          ? Object.keys(TEAM_NAMES_WITH_NUMS).reduce(
              (acc, teamName) =>
                acc.replace(
                  teamName,
                  TEAM_NAMES_WITH_NUMS[teamName as TeamNamesWithNums],
                ),
              outcome.abbreviation,
            )
          : undefined,
      };
    });
  };

  const makeOutcomesNamesNumberSafeReverse = (
    outcomes: ProcessedOutcome[],
  ): ProcessedOutcome[] => {
    return outcomes.filter(identity).map((outcome) => {
      let name = outcome.name;
      Object.keys(TEAM_NAMES_WITH_NUMS).forEach((key) => {
        name = name.replace(
          TEAM_NAMES_WITH_NUMS[key as TeamNamesWithNums],
          key,
        );
      });

      let abbreviation = outcome.abbreviation;

      if (outcome.abbreviation) {
        Object.keys(TEAM_NAMES_WITH_NUMS).forEach((key) => {
          abbreviation = abbreviation?.replace(
            TEAM_NAMES_WITH_NUMS[key as TeamNamesWithNums],
            key,
          );
        });
      }

      return { ...outcome, name, abbreviation };
    });
  };

  const firstPairOutcomes: ProcessedOutcome[] = [];
  const secondPairOutcomes: ProcessedOutcome[] = [];

  const safeOutcomes = makeOutcomesNamesNumberSafe(outcomes);

  for (const outcome of safeOutcomes) {
    if (outcome?.type?.includes(pairs[0])) {
      firstPairOutcomes.push({
        ...outcome,
        value: stringOpposites
          ? outcome.abbreviation ?? outcome.name
          : outcome.abbreviation?.match(findNum)?.[0] ??
            outcome.name?.match(findNum)?.[0],
      });
    } else if (outcome?.type?.includes(pairs[1])) {
      secondPairOutcomes.push({
        ...outcome,
        value: stringOpposites
          ? outcome.abbreviation ?? outcome.name
          : outcome.abbreviation?.match(findNum)?.[0] ??
            outcome.name?.match(findNum)?.[0],
      });
    }
  }

  firstPairOutcomes.sort(
    (a, b) =>
      parseFloat(a.abbreviation ?? "") - parseFloat(b.abbreviation ?? ""),
  );
  secondPairOutcomes.sort(
    (a, b) =>
      parseFloat(b.abbreviation ?? "") - parseFloat(a.abbreviation ?? ""),
  );

  const oppositeOutcomesMatrix = generateOppositeOutcomes(
    firstPairOutcomes.map((outcome) => outcome.value ?? ""),
    secondPairOutcomes.map((outcome) => outcome.value ?? ""),
    pairs,
  );

  // Same rule as marketSearch - a suspended market with nothing active keeps
  // its lines, locked.
  const outcomeList = Object.values(outcomes);
  const isFullySuspended =
    outcomeList.some((outcome) => outcome.market?.status === "SUSPENDED") &&
    outcomeList.every((outcome) => !outcome.active);

  const sortedOutcomes = [] as ProcessedOutcome[];
  for (const num of oppositeOutcomesMatrix) {
    const activeFirst = firstPairOutcomes.find(
      (outcome) => outcome.value === num.first,
    );
    const activeSecond = secondPairOutcomes.find(
      (outcome) => outcome.value === num.second,
    );

    // either existing activeFirst, activeSecond, or the first available in either list
    const templateOutcome =
      activeFirst ||
      activeSecond ||
      firstPairOutcomes[0] ||
      secondPairOutcomes[0];

    if (!templateOutcome) continue;

    const firstOutcome =
      activeFirst ?? createDummyOutcome(templateOutcome, num.first, pairs);
    const secondOutcome =
      activeSecond ?? createDummyOutcome(templateOutcome, num.second, pairs);

    if (
      showInactive ||
      isFullySuspended ||
      firstOutcome.active ||
      secondOutcome.active
    ) {
      /**
       * only one active outcome is required to render both, this keeps the
       * outcome groups even on the match page and the dummy one is always
       * disabled, without odds. It is presentation only.
       * */
      sortedOutcomes.push(firstOutcome);
      sortedOutcomes.push(secondOutcome);
    }
  }

  return makeOutcomesNamesNumberSafeReverse(sortedOutcomes);
};

export const sortOutcomes = (
  outcomes: ProcessedOutcomes,
  isOutright: boolean,
  marketType: string,
  isLiveViewOnly = false,
): ProcessedOutcome[] => {
  // View-only sorts on the price it sees, so live odds don't reshuffle it
  const getOdds = (outcome: ProcessedOutcome) =>
    isLiveViewOnly
      ? getDisplayOdds({
          odds: outcome.odds,
          closingOdds: outcome.closingOdds,
          isLiveViewOnly,
        })
      : outcome.odds;

  // Map to store preprocessed outcomes.
  const outcomesWithInfo = Object.entries(outcomes).map(([id, outcome]) => {
    const name =
      outcome?.abbreviation?.toLowerCase() ?? outcome?.name?.toLowerCase();
    const numberMatch = name?.match(/[-+]?\d*\.?\d+/g);
    const numbersWithDashMatch = name?.match(/[-+]?\d*\.?\d+-[-+]?\d*\.?\d+/g);
    const numbersWithColonMatch = name?.match(/[-+]?\d*\.?\d+:[-+]?\d*\.?\d+/g);

    return {
      id,
      outcome,
      name,
      hasNumber: !!numberMatch,
      number: numberMatch ? parseFloat(numberMatch[0]) : 0,
      numbersWithDash: numbersWithDashMatch
        ? parseFloat(numbersWithDashMatch[0].replace("-", ""))
        : numbersWithColonMatch
          ? parseFloat(numbersWithColonMatch[0].replace(":", ""))
          : 0,
      isOverUnder: name?.includes("over") || name?.includes("under"),
      nameWithoutNumber: name?.replace(numberMatch?.[0] ?? "", ""),
      nameWithoutNumbersWithDash: name?.replace(
        numbersWithDashMatch?.[0] ?? "",
        "",
      ),
    };
  });

  const someOutcomeNamesHaveNumbers = outcomesWithInfo.some(
    (info) => info.hasNumber,
  );

  return outcomesWithInfo
    .sort((infoA, infoB) => {
      const { outcome: outcomeA, name: outcomeAName, number: numAInt } = infoA;
      const { outcome: outcomeB, name: outcomeBName, number: numBInt } = infoB;

      if (!outcomeAName || !outcomeBName) {
        return 0;
      }

      if (!outcomeAName || !outcomeB.name) {
        return 0;
      }

      if (isOutright || marketType === "CUSTOM") {
        return getOdds(outcomeA) - getOdds(outcomeB);
      }

      if (
        marketType === "CORRECT_MAP_SCORE" &&
        outcomeA.abbreviation &&
        outcomeB.abbreviation
      ) {
        return (
          OUTCOME_NAMES.indexOf(outcomeA.abbreviation) -
          OUTCOME_NAMES.indexOf(outcomeB.abbreviation)
        );
      }

      if (
        marketType.includes("CORRECTSCORE") ||
        marketType.includes("CORRECT_SCORE")
      ) {
        if (
          infoA.nameWithoutNumbersWithDash === infoB.nameWithoutNumbersWithDash
        ) {
          return infoA.numbersWithDash - infoB.numbersWithDash;
        }

        return infoA.nameWithoutNumbersWithDash?.localeCompare(
          infoB.nameWithoutNumbersWithDash ?? "",
        );
      }

      if (outcomeAName.includes("player tries")) {
        return getOdds(outcomeA) - getOdds(outcomeB);
      }

      if (outcomeAName.includes("team event")) {
        return outcomeA.name?.localeCompare(outcomeB.name ?? "");
      }

      if (someOutcomeNamesHaveNumbers) {
        if (infoA.isOverUnder && numAInt === numBInt) {
          return outcomeB.name?.localeCompare(outcomeA.name ?? "");
        }

        if (numAInt !== numBInt) {
          return numAInt - numBInt;
        }

        // If the number is the same, then compare by the odds
        return getOdds(outcomeA) - getOdds(outcomeB);
      }

      // Default to sorting by odds
      if (getOdds(outcomeA) && getOdds(outcomeB)) {
        return getOdds(outcomeA) - getOdds(outcomeB);
      }

      return outcomeA.name?.localeCompare(outcomeB.name ?? "");
    })
    .map(({ id }) => ({ ...outcomes[id], id }));
};

export type Direction = "up" | "down";
export const getOddsChangeDirection = (
  prevOdds: number | undefined,
  odds: number,
): Direction | undefined => {
  // if we don't have previous odds then we can't have a direction
  if (!prevOdds || prevOdds === odds) return;

  return prevOdds > odds ? "down" : "up";
};

/**
 * Given an array of values and a max length for each combination - return all combinations of that length
 * @param values
 * @param length
 */
export const getCombinations = (values: number[], length: number) => {
  if (length === 1) return values.map((item) => [item]);

  const combinations: number[][] = [];
  values.forEach((current, index) => {
    const smallerCombinations = getCombinations(
      values.slice(index + 1),
      length - 1,
    );
    smallerCombinations.forEach((combination) => {
      combinations.push([current, ...combination]);
    });
  });
  return combinations;
};

/**
 * Calculate the potential payout (in dollars), if all combos win. Rounding and truncation to match the admin value for "potential payout"
 *    example data:
 *      -  odds:                          1.62, 1.08, 1.95
 *      -  selections Required To Win:    2
 *      -  stake:                         3
 *      -  combination Count:             3
 *      -  expected result (from admin):  $6.99
 *
 *     Stake per combo: stake / combination count (rounded to 2 decimals)
 *          = 3 / 3
 *          = $1
 *
 *     Figure out the combinations and multiply them together, truncate to 2 decimals
 *        a: 1.62, 1.08 => 1.62 x 1.08 = 1.7496 => 1.74
 *        b: 1.62, 1.95 => 1.62 x 1.95 = 3.159 => 3.15
 *        c: 1.08, 1.95 => 1.08 x 1.95 = 2.106 => 2.10
 *
 *    Multiply by the stake and add them together (round to 2 decimals)
 *        a: 1.74 * 1 +
 *        b: 3.15 * 1 +
 *        c: 2.1 * 1 +
 *        = 6.99
 * --------
 * larger example here: https://puntaa.atlassian.net/browse/PKB-7905?focusedCommentId=43907
 *
 * @param odds
 * @param stake
 * @param selectionsRequiredToWin
 */
export const calculatePotentialPayout = (
  odds: (number | string)[],
  stake: number,
  selectionsRequiredToWin: number,
) => {
  const filteredOdds: number[] = odds.filter(
    (odd): odd is number => typeof odd === "number",
  );
  // contains SP or something weird - just return "tbd" (1 is used for sp as well, so bail if there's a 1)
  if (
    filteredOdds.length !== odds.length ||
    filteredOdds.some((odd) => odd === 1)
  ) {
    return "TBD";
  }

  // 1. get all the combinations of odds
  const oddCombos = getCombinations(filteredOdds, selectionsRequiredToWin);
  const combinationsCount = oddCombos.length;

  // 2. get the stake per combo, need to round this
  //    [currency gets ROUNDED to 2 decimals (rounded in BigNumber is decimalPlaces(x, BigNumber.ROUND_HALF_CEIL))]
  const stakePerCombo = new BigNumber(stake)
    .div(combinationsCount)
    .decimalPlaces(2, BigNumber.ROUND_HALF_CEIL)
    .toNumber();

  // 3. figure out the Cumulative Payout for all combos
  const cumulativePayout = oddCombos.reduce((acc, odds) => {
    // the cumulative odds for this combo get multiplied by the stake per combo and added to the result
    const comboOdds: number = odds.reduce(
      (a, b) => new BigNumber(a).multipliedBy(b).toNumber(),
      1,
    );

    // total gets TRUNCATED to 2 decimals here for some reason instead of rounding like currency normally does...
    return (
      acc +
      new BigNumber(comboOdds)
        .multipliedBy(stakePerCombo)
        .decimalPlaces(2, BigNumber.ROUND_DOWN)
        .toNumber()
    );
  }, 0);

  // 4. We return the total as currency which gets ROUNDED to 2 decimals
  return new BigNumber(cumulativePayout)
    .decimalPlaces(2, BigNumber.ROUND_HALF_CEIL)
    .toNumber();
};
